import { CatalogueProduct } from 'dataQueries/purchase.query'
import { bigFromFee } from 'utils/price'

/**
 * Price sort that keeps members of the same backend-computed `documentSelectionGroup` adjacent
 * (ISSUE-2866): the whole group is anchored at its cheapest member's price position, so the
 * mutually-exclusive radio cards read as one block instead of interleaving with unrelated
 * products of the same type (e.g. Teilungserklärung landing between two Bewilligungsurkunden).
 */
export const sortProductsKeepingSelectionGroupsAdjacent = <T extends Pick<CatalogueProduct, 'feePrice' | 'documentSelectionGroup'>>(
  products: T[]
): T[] => {
  const groupAnchor = new Map<string, number>()
  for (const product of products) {
    const group = product.documentSelectionGroup
    if (!group) continue
    const price = bigFromFee(product.feePrice).toNumber()
    const anchor = groupAnchor.get(group)
    if (anchor === undefined || price < anchor) groupAnchor.set(group, price)
  }

  return [...products].sort((a, b) => {
    const aPrice = bigFromFee(a.feePrice).toNumber()
    const bPrice = bigFromFee(b.feePrice).toNumber()
    const aKey = a.documentSelectionGroup ? groupAnchor.get(a.documentSelectionGroup) ?? aPrice : aPrice
    const bKey = b.documentSelectionGroup ? groupAnchor.get(b.documentSelectionGroup) ?? bPrice : bPrice
    if (aKey !== bKey) return aKey - bKey

    // Same anchor position: an ungrouped product with a coincidentally equal price sorts ahead of
    // the block, and within a group the members keep their own price order.
    const aGroup = a.documentSelectionGroup ?? ''
    const bGroup = b.documentSelectionGroup ?? ''
    if (aGroup !== bGroup) return aGroup < bGroup ? -1 : 1
    return aPrice - bPrice
  })
}
